/**
 * Call cards in the comms strip: who is calling and which account, before anyone picks up. Every
 * rep sees every Support call; reps answer in the Dialpad app, so a card opens the account and
 * logs a ticket (with the call), nothing more. Newest first, three shown, the rest counted.
 */
import { useNavigate, Link } from '@tanstack/react-router';
import { type CallCard, type PhoneMatch } from '@lsc/domain';
import { useState } from 'react';
import { phone as formatPhone } from '../../lib/format';
import { openCapture, type CapturePreset } from '../capture/capture-store';
import { resolutionAccounts } from '../lookup/resolution';
import { Button } from '../ui/button';
import { StatusWord } from '../ui/status';

export const ENDED_VISIBLE_MS = 10_000;
const SHOWN = 3;

/** Not ended, or ended under 10 s ago; newest call first. */
/** How long other desks see "Cole picked up" before the card clears. */
export const PICKED_UP_VISIBLE_MS = 3000;

const answeredByOther = (c: CallCard, viewer: string | null) => c.answeredByProfileId !== null && viewer !== null && c.answeredByProfileId !== viewer;

/**
 * Newest first. An ended card stays 10 s. A call someone else answered clears from every other
 * desk 3 s after they pick up (the answering rep keeps it for the whole call); when the answering
 * rep maps to no desk user it stays, as before.
 */
export function visibleCards(cards: CallCard[], now: number, viewerProfileId: string | null = null): CallCard[] {
  return cards
    .filter((c) => {
      if (answeredByOther(c, viewerProfileId)) return c.state === 'connected' && (c.connectedAt ?? now) > now - PICKED_UP_VISIBLE_MS;
      return c.state !== 'ended' || (c.endedAt ?? 0) > now - ENDED_VISIBLE_MS;
    })
    .sort((a, b) => (b.startedAt ?? 0) - (a.startedAt ?? 0));
}

function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, '0')}`;
}

function stateLine(c: CallCard, now: number, viewer: string | null): string {
  if (c.state === 'ringing') return `Ringing ${clock(now - (c.startedAt ?? now))}`;
  if (c.state === 'connected' && answeredByOther(c, viewer)) return `${c.answeredByName ?? 'A rep'} picked up`;
  if (c.state === 'connected') return `With ${c.answeredByName ?? 'a rep'} ${clock(now - (c.connectedAt ?? now))}`;
  return 'Ended';
}

/** What Log a ticket presets for this card (shared with the after-call prompt). */
export function capturePresetFor(c: CallCard, picked?: string): CapturePreset {
  const base: CapturePreset = { rootCallId: c.rootCallId, ...(c.externalNumber ? { phone: c.externalNumber } : {}) };
  const r = c.resolution;
  if (r?.status === 'one') return { accountId: r.match.accountId, ...base };
  if (r?.status === 'many') {
    const id = picked ?? r.suggestedAccountId ?? r.matches[0]?.accountId;
    return id ? { accountId: id, ...base } : base;
  }
  if (r?.status === 'none' && r.draft) return { draftId: r.draft.draftId, ...base };
  return base;
}

/** A short name for the call: the account, the likely location, the draft's store, or the number. */
export function callLabel(c: CallCard): string {
  const r = c.resolution;
  if (r?.status === 'one') return r.match.label;
  if (r?.status === 'many') return (r.matches.find((x) => x.accountId === r.suggestedAccountId) ?? r.matches[0])?.label ?? formatPhone(c.externalNumber) ?? 'the caller';
  if (r?.status === 'none' && r.draft) return r.draft.companyName ?? r.draft.contactName ?? formatPhone(c.externalNumber) ?? 'the caller';
  return formatPhone(c.externalNumber) ?? c.externalNumber ?? 'the caller';
}

function Card({ c, now, viewer }: { c: CallCard; now: number; viewer: string | null }) {
  const navigate = useNavigate();
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const r = c.resolution;
  const number = formatPhone(c.externalNumber) ?? c.externalNumber ?? '';
  const open = (accountId: string) => {
    void navigate({ to: '/accounts/$accountId', params: { accountId } });
  };
  const log = () => {
    openCapture(capturePresetFor(c, picked));
  };

  let who: React.ReactNode;
  let actions: React.ReactNode = null;
  if (r?.status === 'one') {
    who = (
      <>
        <span className="truncate font-medium text-ink">{r.match.label}</span>
        <StatusWord status={r.match.status} className="text-hint text-ink-2" />
      </>
    );
    actions = (
      <Button
        onClick={() => {
          open(r.match.accountId);
        }}
      >
        Open account
      </Button>
    );
  } else if (r?.status === 'many') {
    const all = resolutionAccounts(r);
    const likely = all.find((x) => x.accountId === r.suggestedAccountId) ?? all[0];
    const entity = r.entities[0];
    const name = entity?.legalName ?? likely?.legalName ?? number;
    who = (
      <>
        <span className="truncate font-medium text-ink">{`${name}, ${String(r.total)} locations`}</span>
        {likely && (
          <span className="truncate text-hint text-ink-2">
            {likely.label} <span className="text-info">likely</span>
          </span>
        )}
      </>
    );
    actions = (
      <>
        {likely && (
          <Button
            onClick={() => {
              open(likely.accountId);
            }}
          >
            Open likely
          </Button>
        )}
        <label className="sr-only" htmlFor={`loc-${c.rootCallId}`}>
          Location
        </label>
        <select
          id={`loc-${c.rootCallId}`}
          value={picked ?? likely?.accountId ?? ''}
          onChange={(e) => {
            setPicked(e.target.value);
          }}
          className="h-8 max-w-[180px] rounded-control border border-line bg-surface-2 px-2 text-hint text-ink"
        >
          {all.map((x: PhoneMatch) => (
            <option key={x.accountId} value={x.accountId}>
              {x.label}
            </option>
          ))}
        </select>
      </>
    );
  } else if (r?.status === 'none' && r.draft) {
    who = <span className="truncate text-ink">{`Recognised: ${r.draft.contactName ?? 'unknown caller'}, ${r.draft.companyName ?? 'no store'}, not linked yet`}</span>;
  } else {
    who = (
      <>
        <span className="mono text-ink">{number}</span>
        <span className="text-ink-2">{c.resolutionError ? "Couldn't look this number up" : 'New caller'}</span>
      </>
    );
  }

  return (
    <li className="flex min-w-0 items-center gap-4 border-t border-line px-4 py-2 first:border-t-0">
      <span className={`size-2 shrink-0 rounded-full ${c.state === 'ended' ? 'bg-ink-3' : 'bg-live'}`} aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-0.5 text-body">{who}</div>
      <span className="shrink-0 text-hint text-ink-2">{stateLine(c, now, viewer)}</span>
      {c.ticket && (
        <Link to="/tickets/$ticketId" params={{ ticketId: c.ticket.id }} className="shrink-0 text-hint text-info hover:underline">
          {`Ticket ${c.ticket.number}`}
        </Link>
      )}
      <div className="flex shrink-0 items-center gap-2">
        {actions}
        <Button variant="primary" onClick={log}>
          Log a ticket
        </Button>
      </div>
    </li>
  );
}

export function CallCards({ cards, now, viewerProfileId = null }: { cards: CallCard[]; now: number; viewerProfileId?: string | null }) {
  if (cards.length === 0) return null;
  const shown = cards.slice(0, SHOWN);
  const more = cards.length - shown.length;
  return (
    <section aria-label="Calls" className="call-cards border-b border-line bg-surface">
      <ul>
        {shown.map((c) => (
          <Card key={c.rootCallId} c={c} now={now} viewer={viewerProfileId} />
        ))}
      </ul>
      {more > 0 && <p className="border-t border-line px-4 py-1.5 text-hint text-ink-3">{`and ${String(more)} more`}</p>}
    </section>
  );
}
